import {
  ArrowRight,
  ArrowUpRight,
  Check,
  HelpCircle,
  History,
  Lock,
  Mail,
  RefreshCw,
  ShieldCheck,
  Signal,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/common/loader";
import { PageHeader } from "@/components/common/page-header";
import { ConnectDialog } from "@/features/eservices/components/connect-dialog";
import { RedirectIrsDialog } from "@/features/eservices/components/redirect-irs-dialog";
import { getStatus, setEsMonitor } from "@/features/eservices/api";
import type { ConnectionState, EServicesStatus } from "@/features/eservices/types";
import { cn } from "@/lib/utils";

const STATE_LABEL: Record<ConnectionState, string> = {
  DISCONNECTED: "Disconnected",
  CONNECTING: "Connecting",
  CONNECTED: "Connected",
  BUSY: "Busy",
  EXPIRED: "Session expired",
};

const STATE_COLOR: Record<ConnectionState, string> = {
  DISCONNECTED: "bg-muted-foreground",
  CONNECTING: "bg-amber-500",
  CONNECTED: "bg-emerald-500",
  BUSY: "bg-amber-500",
  EXPIRED: "bg-destructive",
};

const PRO_TIPS = [
  ["Too many CAF failures", "The IRS locked the CAF lookup after repeated failures. Wait and retry later."],
  ["Too many 401 errors", "e-Services rejected the login repeatedly. Re-check the username/password and CAF."],
  ["Server busy", "The IRS TDS is busy. The connector will retry your queued pulls automatically."],
  ["Session expired", "IRS sessions last about an hour. Reconnect to continue pulling transcripts."],
  ["Unauthorized", "This practitioner isn't authorized for the requested taxpayer. Check the CAF/POA."],
  ["No transcripts selected", "Select at least one transcript request before pulling."],
];

type ServiceAction = {
  /** "outline" = ghost/bordered action; "soft" = soft-blue filled action. */
  kind: "outline" | "soft";
  label: string;
  to?: string;
  onClick?: () => void;
  trailingIcon?: typeof ArrowRight;
};

interface ServiceCard {
  icon: typeof ShieldCheck;
  label: string;
  desc: string;
  /** Big numeric figure (counts) — or `pill` for a status chip instead. */
  figure?: string;
  pill?: string;
  actions: ServiceAction[];
}

function ServiceActionButton({ action }: { action: ServiceAction }) {
  const Icon = action.trailingIcon;
  const inner = (
    <>
      {action.label}
      {Icon && <Icon className="size-3.5" />}
    </>
  );
  const className =
    action.kind === "soft"
      ? "bg-info-soft text-info hover:bg-info-soft/80 shadow-none"
      : undefined;
  if (action.to) {
    return (
      <Button asChild size="sm" variant={action.kind === "soft" ? "default" : "outline"} className={className}>
        <Link to={action.to}>{inner}</Link>
      </Button>
    );
  }
  return (
    <Button
      size="sm"
      variant={action.kind === "soft" ? "default" : "outline"}
      className={className}
      onClick={action.onClick}
    >
      {inner}
    </Button>
  );
}

export function EServicesHubPage() {
  const [status, setStatus] = useState<EServicesStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [redirectOpen, setRedirectOpen] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    getStatus()
      .then(setStatus)
      .catch(() => toast.error("Could not load e-Services status."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(refresh, [refresh]);

  const toggleMonitor = async (value: boolean) => {
    if (!status) return;
    setStatus({ ...status, esMonitor: value });
    try {
      await setEsMonitor(value);
    } catch {
      toast.error("Could not update the preference.");
      setStatus({ ...status, esMonitor: !value });
    }
  };

  if (loading || !status) return <Loader />;

  const connected = status.state === "CONNECTED";
  // Readiness is static until the backend exposes CAF-link / ID.me status.
  // TODO(api): wire the first two steps to real connector readiness flags.
  const readySteps = [
    { done: true, title: "IRS e-Services account linked", sub: "Practitioner · CAF on file" },
    { done: true, title: "ID.me verification complete", sub: "Identity confirmed for this firm" },
    {
      done: connected,
      title: "Authorize today's session",
      sub: "Press Connect to IRS and approve the login prompt",
    },
  ];
  const readyDone = readySteps.filter((s) => s.done).length;

  const serviceCards: ServiceCard[] = [
    {
      icon: ShieldCheck,
      label: "Pending POAs",
      figure: String(status.pendingPoaCount),
      desc: "Forms 2848 / 8821 submitted and awaiting CAF unit processing.",
      actions: [
        { kind: "outline", label: "View submissions", to: "/eservices/poa" },
        {
          kind: "soft",
          label: "Submit a POA",
          onClick: () => setRedirectOpen(true),
          trailingIcon: ArrowUpRight,
        },
      ],
    },
    {
      icon: Users,
      label: "CAF monitors",
      figure: String(status.pendingMonitorCount),
      desc: "Clients watched for new authorizations, notices and transcript changes.",
      actions: [
        { kind: "outline", label: "Monitoring report", to: "/eservices/caf-monitor" },
        { kind: "soft", label: "Add a client", to: "/clients" },
      ],
    },
    {
      icon: Mail,
      label: "Secure mailbox",
      pill: status.lastMonitorCheck
        ? new Date(status.lastMonitorCheck).toLocaleDateString()
        : "Never checked",
      desc: "IRS messages, CAF approvals and rejections land here. Checked on every connection.",
      actions: [
        { kind: "soft", label: "Open mailbox", to: "/eservices/mailbox", trailingIcon: ArrowRight },
      ],
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Workspace · e-Services"
        title="IRS e-Services"
        subtitle="Authorize a session, track CAF approvals, read the secure mailbox and pull transcripts — all from one place."
        actions={
          <>
            <Button variant="outline" onClick={() => setTipsOpen(true)}>
              <HelpCircle className="size-4" /> How it works
            </Button>
            <Button variant="cta" onClick={() => setConnectOpen(true)}>
              <Signal className="size-4" /> Connect to IRS
            </Button>
          </>
        }
      />

      {status.eservicesDown && (
        <Alert variant="destructive">
          <AlertTitle>e-Services is unavailable</AlertTitle>
          <AlertDescription>
            {status.eservicesMessage || "Transcript pulls are paused. Try again later."}
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Connection hero (navy) */}
        <Card className="bg-brand-panel relative gap-5 overflow-hidden border-0 p-6 text-white lg:col-span-2">
          <span
            aria-hidden
            className="bg-primary/30 pointer-events-none absolute -top-20 -right-20 size-64 rounded-full blur-3xl"
          />
          <div className="relative flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3.5">
              <span className="relative flex size-12 items-center justify-center rounded-[15px] bg-white/10">
                <Signal className="size-[22px]" />
                <span
                  aria-hidden
                  className={`ring-brand-panel absolute -right-0.5 -bottom-0.5 size-3.5 rounded-full ring-[3px] ${STATE_COLOR[status.state]}`}
                />
              </span>
              <div className="flex flex-col gap-0.5 leading-tight">
                <span className="font-heading text-xl font-semibold tracking-[-0.015em]">
                  {connected ? "Connected" : STATE_LABEL[status.state]}
                </span>
                <span className="text-brand-panel-muted text-[13px]">
                  No active IRS session · last session <strong className="font-semibold text-white">never</strong>
                </span>
              </div>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white/12 px-3 py-1.5 text-xs font-semibold">
              <Lock className="size-3.5" /> Connector · out-of-band
            </span>
          </div>

          <div className="relative h-px bg-white/12" />

          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-1 items-start gap-3">
              <Checkbox
                id="es-monitor"
                checked={status.esMonitor}
                onCheckedChange={(v) => toggleMonitor(Boolean(v))}
                className="mt-0.5 border-white/40"
              />
              <Label htmlFor="es-monitor" className="flex flex-col items-start gap-0.5 text-sm font-normal text-white">
                <span className="font-semibold">Weekly transcript monitoring stays on</span>
                <span className="text-brand-panel-muted text-[13px]">
                  Auto-connects every Monday at 6:00 AM to refresh transcripts for monitored
                  clients.
                </span>
              </Label>
            </div>
            <Button size="sm" className="border border-white/20 bg-white/10 text-white shadow-none hover:bg-white/20">
              <RefreshCw className="size-4" /> Run monitoring now
            </Button>
          </div>
        </Card>

        {/* Ready to connect? checklist */}
        <Card className="flex-col gap-4 p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-lg font-semibold">Ready to connect?</h2>
            <span className="text-muted-foreground text-xs font-semibold">
              {readyDone} of {readySteps.length}
            </span>
          </div>
          <ol className="flex flex-col gap-3">
            {readySteps.map((step, i) => (
              <li key={step.title} className="flex items-start gap-3">
                <span
                  className={cn(
                    "flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    step.done
                      ? "bg-success-soft text-success"
                      : "border-border text-muted-foreground border",
                  )}
                >
                  {step.done ? <Check className="size-3.5" /> : i + 1}
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-[13px] font-semibold">{step.title}</span>
                  <span className="text-muted-foreground text-xs">{step.sub}</span>
                </span>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {serviceCards.map((card) => (
          <Card key={card.label} className="flex-col gap-4 p-5">
            <div className="flex items-start justify-between gap-3">
              <span className="bg-info-soft text-info flex size-11 items-center justify-center rounded-[14px]">
                <card.icon className="size-5" />
              </span>
              {card.figure != null ? (
                <span className="font-heading text-[2rem] leading-none font-semibold tabular-nums">
                  {card.figure}
                </span>
              ) : (
                <span className="bg-muted text-muted-foreground rounded-full px-2.5 py-1 text-xs font-semibold">
                  {card.pill}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="font-heading text-lg font-semibold">{card.label}</h2>
              <p className="text-muted-foreground text-[13px]">{card.desc}</p>
            </div>
            <div className="mt-auto flex flex-wrap gap-2 pt-1">
              {card.actions.map((action) => (
                <ServiceActionButton key={action.label} action={action} />
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Session history */}
      <Card className="flex-col gap-3 p-6">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold">Session history</h2>
          <span className="text-muted-foreground text-xs font-semibold">Last 30 days</span>
        </div>
        {/* TODO(api): list real connection sessions once the backend exposes them. */}
        <div className="border-border bg-surface-hover flex flex-wrap items-center gap-4 rounded-2xl border border-dashed px-5 py-5">
          <span className="bg-info-soft text-info flex size-12 shrink-0 items-center justify-center rounded-full">
            <History className="size-6" />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-foreground font-semibold">No sessions yet</span>
            <span className="text-muted-foreground text-[13px]">
              Every connection is logged here with who authorized it, what was pulled and when it
              ended.
            </span>
          </div>
        </div>
      </Card>

      <ConnectDialog
        open={connectOpen}
        onOpenChange={setConnectOpen}
        onConnected={refresh}
      />
      <RedirectIrsDialog
        open={redirectOpen}
        onOpenChange={setRedirectOpen}
        target="poa"
      />

      <Dialog open={tipsOpen} onOpenChange={setTipsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>e-Services troubleshooting</DialogTitle>
            <DialogDescription>Common connection messages.</DialogDescription>
          </DialogHeader>
          <ul className="flex flex-col gap-3">
            {PRO_TIPS.map(([title, body]) => (
              <li key={title}>
                <p className="text-foreground text-sm font-medium">{title}</p>
                <p className="text-muted-foreground text-sm">{body}</p>
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
